import { Components } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { UserFilledIcon } from '@/src/components/icon/filled/UserFilledIcon';
import { getFocusRingStyle, updateFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export const flowAvatar: Components<Theme>['MuiAvatar'] = {
  defaultProps: {
    variant: 'circular',
    size: 'md',
    // default avatar
    children: <UserFilledIcon />,
  },
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      overflow: 'visible',
      color: theme.palette.brand.tertiary,
      backgroundColor: theme.palette.brand.quarternary,
      '&:has(.MuiAvatar-img)': {
        color: theme.palette.onSurface.os500,
        backgroundColor: theme.palette.surface.primary,
        ...((!ownerState.border || ownerState.border === 'auto') && {
          border: `1px solid currentColor`,
        }),
      },
      // font, rest is set in the variants bellow
      letterSpacing: '0.02em',
      fontWeight: '700',
      // optional border
      ...(ownerState.border === 'on' && {
        border: `1px solid currentColor`,
      }),
      // custom 'disabled' state
      ...(ownerState.disabled && {
        '&, &:has(.MuiAvatar-img)': {
          color: theme.palette.disabled.tertiary,
          backgroundColor: theme.palette.surface.secondary,
        },
      }),
      // avatar img
      '& .MuiAvatar-img': {
        borderRadius: 'inherit',
        // custom 'disabled' state
        ...(ownerState.disabled && {
          opacity: '0.65',
        }),
      },
      // button
      '&.MuiButtonBase-root': {
        // focused style
        ...getFocusRingStyle(theme, true, 0),
        '&:has(.MuiAvatar-img)': {
          ...((!ownerState.border || ownerState.border === 'auto') && {
            ...updateFocusRingStyle(1),
          }),
        },
        ...(ownerState.border === 'on' && {
          ...updateFocusRingStyle(1),
        }),
        // pseudo background for hover
        ':after': {
          display: 'block',
          content: '""',
          position: 'absolute',
          inset: '0',
          borderRadius: 'inherit',
          backgroundColor: theme.palette.transparent.primary,
          transition: 'opacity 0.3s ease-out',
          opacity: '0',
        },
        // interactive style
        ...getInteractiveStyle(
          {
            '&:after': {
              opacity: '0',
            },
          },
          {
            '&:after': {
              opacity: '1',
            },
          },
        ),
      },
    }),
    rounded: {
      // rounded variant is used in listItemAvatar
      borderRadius: '4px',
    },
  },
  variants: [
    // size property
    {
      props: { size: 'sm' },
      style: {
        width: '24px',
        height: '24px',
        lineHeight: '24px',
        fontSize: '10px',
      },
    },
    {
      props: { size: 'md' },
      style: {
        width: '44px',
        height: '44px',
        lineHeight: '44px',
        fontSize: '14px',
      },
    },
    {
      props: { size: 'lg' },
      style: {
        width: '56px',
        height: '56px',
        lineHeight: '56px',
        fontSize: '20px',
      },
    },
  ],
};
